import InterventionEmpfehlungen from "../components/InterventionEmpfehlungen";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import { usePerspective } from "../lib/perspective";
import { useRealtime } from "../lib/useRealtime";
import Hint from "../components/Hint";
import { DeepLinkBar } from "../components/DeepLink";
import { dashboardUrl, openProjectProgramUrl, evidenceRoute } from "../lib/deeplinks";
import {
  CORE_PHASE_COUNT,
  PROCESS_AGENTS,
  PROCESS_PHASES,
  allProcessSteps,
  findAgent,
  type ProcessAgent,
  type ProcessPhase,
  type ProcessStep,
} from "../lib/processLandscape";

type ViewMode = "timeline" | "grid";
type StreamFilter = "Alle" | ProcessPhase["stream"];
type ProcessProgressSnapshot = {
  completed_steps: string[];
  completed_count: number;
  total_steps: number;
  progress_pct: number;
};

type GuideRaciInput = { rolle: string; label: string; liefert: string };
type GuideRaci = {
  inputs_von: GuideRaciInput[];
  verantwortlich: string;
  rechenschaft: string;
  konsultiert: string;
  informiert: string;
  abschluss_durch: string;
  abschluss_rollen: string[];
};
type GuideAblage = { was: string; wo: string; sichtbar: string };
type GuideContribution = {
  id: string;
  step_id: string;
  kind: string;
  text?: string | null;
  url?: string | null;
  author?: string | null;
  role?: string | null;
  created_at: string;
};
type GuideRun = {
  run_id: string;
  status: string;
  started_at?: string | null;
  ended_at?: string | null;
  evidence_note?: string | null;
  actor_role?: string | null;
};
type StepGuideResponse = {
  step: { id: string; title: string };
  viewer_role: string;
  zweck?: string | null;
  fragen: string[];
  fragen_quelle: string;
  raci: GuideRaci;
  ablage: GuideAblage[];
  oe_board: { mapped: boolean; ressort?: string | null; oe_phase?: string | null; hinweis: string };
  openproject: { write_enabled: boolean; modus: string; hinweis: string };
  contributions: GuideContribution[];
  runs: GuideRun[];
};
type KnowledgeItem = {
  filename?: string | null;
  path?: string | null;
  source?: string | null;
  snippet?: string | null;
  ingested_at?: string | null;
};
type KnowledgeResponse = { query: string; engine?: string | null; error?: string | null; items: KnowledgeItem[] };
type AnalyzeResponse = {
  answer: string;
  model?: string | null;
  degraded: boolean;
  knowledge_used: (string | null)[];
  fragen_quelle?: string | null;
};
type StepRunEffects = {
  runs_index?: { written?: boolean; index?: string; reason?: string } | null;
  oe_board?: { applied?: boolean; mode?: string; doc_id?: string; oe_phase?: string; reason?: string } | null;
  openproject?: {
    mode?: string;
    wp?: { wp_id?: number | string; subject?: string; url?: string } | null;
    comment_preview?: string;
    freigabe_hinweis?: string;
    reason?: string;
  } | null;
};
type StepRunResponse = {
  run_id: string;
  status: string;
  process_step_id: string;
  evidence_bundle?: string | null;
  effects?: StepRunEffects | null;
};

const STREAMS: StreamFilter[] = ["Alle", "Analyse & Definition", "Umsetzung", "Nachhaltigkeit"];

export default function ProcessLandscapePage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const { connected, events } = useRealtime();
  const [view, setView] = useState<ViewMode>("timeline");
  const [stream, setStream] = useState<StreamFilter>("Alle");
  const [selectedAgentId, setSelectedAgentId] = useState("kpi-mining");
  const [selectedStepId, setSelectedStepId] = useState<string | null>("1.2.4");
  const [stepActionPending, setStepActionPending] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [progressSnapshot, setProgressSnapshot] = useState<ProcessProgressSnapshot | null>(null);

  const allSteps = useMemo(() => allProcessSteps(), []);
  useEffect(() => {
    if (!effectiveTenantId) return;
    let cancelled = false;
    api.get<ProcessProgressSnapshot>("/process-landscape/progress")
      .then((progress) => {
        if (!cancelled) setProgressSnapshot(progress);
      })
      .catch(() => {
        if (!cancelled) setProgressSnapshot(null);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveTenantId]);

  const completedStepIds = useMemo(() => {
    const ids = new Set<string>();
    for (const stepId of progressSnapshot?.completed_steps ?? []) {
      ids.add(stepId);
    }
    for (const e of events) {
      const type = e.type.toLowerCase();
      const status = String(e.payload?.status ?? "").toLowerCase();
      const stepId = e.payload?.process_step_id ?? e.payload?.step_id;
      if (stepId && (type.includes("process") || type.includes("agent")) && (status === "succeeded" || status === "completed")) {
        ids.add(String(stepId));
      }
    }
    return ids;
  }, [events, progressSnapshot]);

  const visiblePhases = useMemo(
    () => PROCESS_PHASES.filter((phase) => stream === "Alle" || phase.stream === stream),
    [stream],
  );
  const selectedAgent = findAgent(selectedAgentId) ?? PROCESS_AGENTS[0];
  const selectedStep = selectedStepId ? allSteps.find(({ id }) => id === selectedStepId) : null;
  const completed = allSteps.filter(({ id }) => completedStepIds.has(id)).length;
  const progressPct = Math.round((completed / allSteps.length) * 100);
  const lastLiveEvents = events
    .filter((e) => e.type.includes("agent") || e.type.includes("process") || e.type.includes("quality"))
    .slice(0, 5);

  function refreshProgress() {
    api.get<ProcessProgressSnapshot>("/process-landscape/progress")
      .then(setProgressSnapshot)
      .catch(() => undefined);
  }

  async function startStep(step: ProcessStep) {
    const key = `start:${step.id}`;
    setStepActionPending(key);
    setLastAction(null);
    try {
      const res = await api.post<StepRunResponse>(
        `/process-landscape/steps/${encodeURIComponent(step.id)}/start`,
        { requested_by: "process-landscape-ui", mode: "execute" },
      );
      setLastAction(
        `${step.id} ${step.title}: Vorgang gestartet — als ${res.run_id} im Protokoll (Agent Runs & Evidence) registriert.`,
      );
    } catch (err: any) {
      setLastAction(stepErrorMessage(step, err));
    } finally {
      setStepActionPending(null);
    }
  }

  return (
    <section className="process-page">
      <header className="process-hero">
        <div>
          <p className="hero-eyebrow">Prozesslandschaft · Tenant {effectiveTenantId}</p>
          <h1>Prozesslandschaft — von der Analyse bis zum Betrieb</h1>
          <p className="hero-sub">
            {CORE_PHASE_COUNT} Hauptphasen, {allSteps.length} Teilprozesse und {PROCESS_AGENTS.length} spezialisierte KI-Agenten.
            Jeder erledigte Schritt kann hier als prüfbarer Vorgang („Run“) mit Nachweis („Evidence“) protokolliert werden.
            Realtime {connected ? <span className="dot-live">● verbunden</span> : <span className="dot-off">○ offline</span>}.
          </p>
        </div>
        <div className="process-hero-actions">
          <Link to={advisorLink(selectedAgent, selectedStep?.title)} className="btn primary" title="Öffnet den KI-Berater mit dem Kontext des ausgewählten Prozessschritts.">KI-Berater fragen</Link>
          <Link to="/app/agents" className="btn ghost" title="Alle protokollierten Vorgänge (Runs) mit ihren Nachweisen ansehen.">Protokoll: Agent Runs</Link>
        </div>
      </header>

      <section className="pl-explainer" aria-label="Erklärung: Was ist die Prozesslandschaft?">
        <h2>Was sehen Sie hier — und was können Sie tun?</h2>
        <p>
          Diese Seite zeigt den <strong>Standard-Fahrplan der Transformation</strong>: {CORE_PHASE_COUNT} Hauptphasen
          mit {allSteps.length} Teilprozessen — von der Analyse bis zum laufenden Betrieb. Klicken Sie einen Schritt an:
          Der <strong>geführte Modus</strong> erklärt dann, was in dem Schritt passiert, wer was liefert, wie das Ergebnis
          entsteht, wer abschließt und was wo abgelegt wird.
        </p>
        <ul className="pl-expl-points">
          <li>
            <strong>Vorgang starten</strong>
            <Hint term="prozess_run" /> — legt einen protokollierten Durchlauf („Run“) dieses Schritts an.
            Er erscheint sofort im Protokoll unter <Link to="/app/agents">Agent Runs &amp; Evidence</Link>.
          </li>
          <li>
            <strong>Mit Nachweis abschließen</strong>
            <Hint term="prozess_evidence" /> — Sie notieren kurz, was erledigt wurde („Evidence“ = Beleg).
            Erst damit gilt der Schritt als abgeschlossen und zählt in den Fortschritt — nachvollziehbar für Audit und Compliance.
            Der Abschluss wirkt zusätzlich in Runs-Index, OE-Board und (nach Freigabe) OpenProject.
          </li>
          <li>
            <strong>Eingaben &amp; Analyse</strong>
            <Hint term="wissensbasis" /> — Kunde und Berater:in hinterlegen Notizen und Links direkt am Schritt.
            Die Wissensbasis liefert dazu in Echtzeit passende Dokumente; der KI-Berater analysiert auf Wunsch den Stand.
          </li>
        </ul>
        <p className="muted small">
          Es wird nichts simuliert: Der Fortschritt zählt nur Vorgänge, die hier tatsächlich ausgelöst wurden.
        </p>
      </section>

      <section className="process-summary">
        <Metric label="Hauptphasen" value={CORE_PHASE_COUNT} note="Analyse bis Betrieb" />
        <Metric label="Teilprozesse" value={allSteps.length} note={`${completed} live erledigt`} />
        <Metric label="KI-Agenten" value={PROCESS_AGENTS.length} note="tool- und policy-gebunden" />
        <div className="process-progress-card">
          <div className="process-progress-head">
            <span>Gesamtfortschritt</span>
            <strong>{completed} von {allSteps.length}</strong>
          </div>
          <div className="process-progress-track">
            <div className="process-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <p className="muted small">
            Ohne echte Prozessereignisse bleibt der Fortschritt bei 0 %. Das ist absichtlich: keine erfundene Umsetzung für echte Mandanten.
          </p>
        </div>
      </section>

      <section className="runtime-strip">
        <RuntimeCard
          title="Research & Benchmark"
          body="Crawl4AI und Nimble liefern nur öffentliche oder lizenzierte Signale. OpenRouter bleibt policy-gated und anonymisiert."
          status="Crawl4AI · Nimble · SearXNG"
        />
        <RuntimeCard
          title="Sensitive Reasoning"
          body="Vertrauliche Analyse läuft lokal über DeepSeek V4 Flash 284B oder Qwen-Fallback hinter LiteLLM."
          status="local-first"
        />
        <RuntimeCard
          title="Evidence & HITL"
          body="Jeder Agent-Run braucht Tenant-ID, Trace, Evidence-Bundle und Freigabe, bevor externe Aktionen laufen."
          status="SelfCLAW"
        />
      </section>

      <section className="process-workbench">
        <aside className="agent-panel">
          <div className="section-head">
            <h2>{PROCESS_AGENTS.length} KI-Agenten</h2>
            <span className="muted small">proaktive Trigger</span>
          </div>
          <div className="agent-grid">
            {PROCESS_AGENTS.map((agent) => (
              <button
                key={agent.id}
                className={`agent-chip ${agent.id === selectedAgent.id ? "active" : ""}`}
                onClick={() => setSelectedAgentId(agent.id)}
                title={agent.domain}
              >
                <span>{agent.code}</span>
                <strong>{agent.name}</strong>
              </button>
            ))}
          </div>
          <AgentDetail agent={selectedAgent} selectedStepTitle={selectedStep?.title} />
        </aside>

        <main className="process-main-panel">
          <div className="process-toolbar">
            <div className="segmented">
              <button className={view === "timeline" ? "active" : ""} onClick={() => setView("timeline")}>Timeline</button>
              <button className={view === "grid" ? "active" : ""} onClick={() => setView("grid")}>Grid</button>
            </div>
            <div className="segmented wide">
              {STREAMS.map((item) => (
                <button key={item} className={stream === item ? "active" : ""} onClick={() => setStream(item)}>{item}</button>
              ))}
            </div>
          </div>

          <div className="stream-overview">
            {(["Analyse & Definition", "Umsetzung", "Nachhaltigkeit"] as const).map((name) => {
              const streamSteps = allSteps.filter(({ phase }) => phase.stream === name);
              const done = streamSteps.filter(({ id }) => completedStepIds.has(id)).length;
              const pct = streamSteps.length ? Math.round((done / streamSteps.length) * 100) : 0;
              return (
                <article key={name}>
                  <span>{name}</span>
                  <strong>{pct}%</strong>
                  <div className="mini-progress"><div style={{ width: `${pct}%` }} /></div>
                  <p className="muted small">{done} von {streamSteps.length} Teilprozessen</p>
                </article>
              );
            })}
          </div>

          {lastAction && <div className="process-action-status">{lastAction}</div>}

          <div className={`phase-list ${view}`}>
            {visiblePhases.map((phase) => (
              <PhaseCard
                key={phase.id}
                phase={phase}
                completedStepIds={completedStepIds}
                selectedStepId={selectedStepId}
                onSelectStep={(step) => {
                  setSelectedStepId(step.id);
                  setSelectedAgentId(step.agentId);
                }}
                onStartStep={(step) => startStep(step)}
                stepActionPending={stepActionPending}
                onCompleted={(message) => {
                  setLastAction(message);
                  refreshProgress();
                }}
              />
            ))}
          </div>
        </main>
      </section>

      <section className="process-live">
        <div className="section-head">
          <h2>Echtzeit-Agentenfeed</h2>
          <span className="muted small">{connected ? "SSE verbunden" : "SSE offline"} · {lastLiveEvents.length}</span>
        </div>
        {lastLiveEvents.length === 0 ? (
          <div className="empty-live">
            <strong>Noch keine Prozessereignisse für diesen Mandanten.</strong>
            <p className="muted">
              Sobald hier Vorgänge gestartet, Schritte mit Nachweis abgeschlossen oder Quality-Checks
              ausgeführt werden, erscheinen die Ereignisse sofort hier und im Command Center.
            </p>
          </div>
        ) : (
          <ul className="live-event-list">
            {lastLiveEvents.map((event) => (
              <li key={event.id}>
                <span className="live-dot" />
                <div>
                  <strong>{event.payload?.agent ?? event.type}</strong>
                  <p className="muted small">{event.payload?.summary ?? event.payload?.status ?? event.type}</p>
                </div>
                <time>{new Date(event.ts * 1000).toLocaleTimeString()}</time>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

function stepErrorMessage(step: ProcessStep, err: any): string {
  if (err?.status === 403) {
    return `${step.id}: Diese Aktion ist der Beraterrolle vorbehalten. Als Kunde können Sie Eingaben (Notizen, Links) zum Schritt beitragen — der Abschluss erfolgt durch Ihre Berater:in.`;
  }
  return `${step.id}: Aktion fehlgeschlagen (HTTP ${err?.status ?? "Fehler"}) — bitte erneut versuchen.`;
}

function Metric({ label, value, note }: { label: string; value: number; note: string }) {
  return (
    <article className="process-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{note}</p>
    </article>
  );
}

function RuntimeCard({ title, body, status }: { title: string; body: string; status: string }) {
  return (
    <article className="runtime-card">
      <span>{status}</span>
      <strong>{title}</strong>
      <p>{body}</p>
    </article>
  );
}

function AgentDetail({ agent, selectedStepTitle }: { agent: ProcessAgent; selectedStepTitle?: string }) {
  return (
    <article className="agent-detail">
      <div className="agent-detail-head">
        <span className="agent-code-large">{agent.code}</span>
        <div>
          <h3>{agent.name}</h3>
          <p className="muted small">{agent.domain}</p>
        </div>
      </div>
      <p>{agent.mission}</p>
      <dl className="agent-kv">
        <dt title="Welches KI-Modell diesen Agenten antreibt. 'lokal' bedeutet: vertrauliche Daten verlassen das System nicht.">Modell-Route</dt>
        <dd>{agent.modelRoute}</dd>
        <dt title="Diese Belege erzeugt der Agent bei jedem Lauf — Grundlage für Audit und Nachvollziehbarkeit.">Evidence (Belege)</dt>
        <dd>{agent.evidence}</dd>
      </dl>
      <div className="chip-block">
        <span>Tools</span>
        <div>{agent.tools.map((tool) => <em key={tool}>{tool}</em>)}</div>
      </div>
      <div className="chip-block">
        <span>Proaktive Trigger</span>
        <div>{agent.proactiveTriggers.map((trigger) => <em key={trigger}>{trigger}</em>)}</div>
      </div>
      <div className="agent-detail-actions">
        <Link to={advisorLink(agent, selectedStepTitle)} className="btn primary small" title="Öffnet den KI-Berater in der Rolle dieses Agenten (mit Schritt-Kontext).">Berater mit Kontext</Link>
        <Link to={`/app/agents?agent=${encodeURIComponent(agent.id)}`} className="btn ghost small" title="Protokollierte Vorgänge (Runs) dieses Agenten ansehen.">Vorgänge (Runs)</Link>
      </div>
    </article>
  );
}

function PhaseCard({
  phase,
  completedStepIds,
  selectedStepId,
  onSelectStep,
  onStartStep,
  stepActionPending,
  onCompleted,
}: {
  phase: ProcessPhase;
  completedStepIds: Set<string>;
  selectedStepId: string | null;
  onSelectStep: (step: ProcessStep) => void;
  onStartStep: (step: ProcessStep) => void;
  stepActionPending: string | null;
  onCompleted: (message: string) => void;
}) {
  const done = phase.steps.filter((step) => completedStepIds.has(step.id)).length;
  const pct = Math.round((done / phase.steps.length) * 100);
  return (
    <article className="phase-card">
      <header className="phase-card-head">
        <div>
          <span className="phase-index">{phase.id}</span>
          <h3>{phase.title}</h3>
          <p>{phase.subtitle}</p>
        </div>
        <div className="phase-progress">
          <strong>{pct}%</strong>
          <span>{done}/{phase.steps.length}</span>
        </div>
      </header>
      <p className="phase-objective">{phase.objective}</p>
      <ul className="process-step-list">
        {phase.steps.map((step) => {
          const agent = findAgent(step.agentId);
          const selected = selectedStepId === step.id;
          const doneStep = completedStepIds.has(step.id);
          return (
            <li key={step.id} className={`process-step ${selected ? "selected" : ""} ${doneStep ? "done" : ""}`}>
              <button className="step-select" onClick={() => onSelectStep(step)}>
                <span className="step-id">{step.id}</span>
                <span className="step-copy">
                  <strong>{step.title}</strong>
                  <small>{step.description}</small>
                </span>
                <span className="step-agent">{agent?.code ?? "AI"}</span>
              </button>
              {selected && (
                <StepGuide
                  step={step}
                  agent={agent}
                  doneStep={doneStep}
                  stepActionPending={stepActionPending}
                  onStartStep={onStartStep}
                  onCompleted={onCompleted}
                />
              )}
            </li>
          );
        })}
      </ul>
    </article>
  );
}

/**
 * Der GEFÜHRTE Prozessschritt: fünf nummerierte Bereiche nehmen Kunde und
 * Berater:in an die Hand — was passiert, wer liefert was, wie entsteht das
 * Ergebnis (Fragen + Wissensbasis + KI-Analyse), wer schließt ab, was wird
 * wo abgelegt. Alle Daten kommen live aus dem Orchestrator.
 */
function StepGuide({
  step,
  agent,
  doneStep,
  stepActionPending,
  onStartStep,
  onCompleted,
}: {
  step: ProcessStep;
  agent: ProcessAgent | undefined;
  doneStep: boolean;
  stepActionPending: string | null;
  onStartStep: (step: ProcessStep) => void;
  onCompleted: (message: string) => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const { effectiveTenantId } = useTenant();
  const { perspective } = usePerspective();
  const isKunde = perspective === "kunde";

  const [knowledgeQuery, setKnowledgeQuery] = useState("");
  const [contribKind, setContribKind] = useState<"notiz" | "link" | "datei">("notiz");
  const [contribText, setContribText] = useState("");
  const [contribUrl, setContribUrl] = useState("");
  const [analysis, setAnalysis] = useState<AnalyzeResponse | null>(null);
  const [evidenceNote, setEvidenceNote] = useState("");
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [effects, setEffects] = useState<StepRunEffects | null>(null);
  const evidenceRef = useRef<HTMLTextAreaElement | null>(null);

  const guide = useQuery({
    queryKey: ["step-guide", effectiveTenantId, step.id],
    queryFn: () => api.get<StepGuideResponse>(`/process-landscape/steps/${encodeURIComponent(step.id)}/guide`),
    retry: 1,
  });

  const knowledge = useQuery({
    queryKey: ["step-knowledge", effectiveTenantId, step.id, knowledgeQuery],
    queryFn: () =>
      api.get<KnowledgeResponse>(
        `/process-landscape/steps/${encodeURIComponent(step.id)}/knowledge${knowledgeQuery ? `?q=${encodeURIComponent(knowledgeQuery)}` : ""}`,
      ),
    retry: 0,
    staleTime: 30_000,
  });

  const addContribution = useMutation({
    mutationFn: (body: { kind: string; text?: string; url?: string }) =>
      api.post<GuideContribution>(`/process-landscape/steps/${encodeURIComponent(step.id)}/contributions`, body),
    onSuccess: () => {
      setContribText("");
      setContribUrl("");
      qc.invalidateQueries({ queryKey: ["step-guide", effectiveTenantId, step.id] });
    },
  });

  const analyze = useMutation({
    mutationFn: () => api.post<AnalyzeResponse>(`/process-landscape/steps/${encodeURIComponent(step.id)}/analyze`, {}),
    onSuccess: (data) => setAnalysis(data),
  });

  const complete = useMutation({
    mutationFn: () =>
      api.post<StepRunResponse>(`/process-landscape/steps/${encodeURIComponent(step.id)}/complete`, {
        requested_by: "process-landscape-ui",
        mode: "complete",
        evidence_note: evidenceNote.trim(),
        analysis_summary: analysis?.answer ? analysis.answer.slice(0, 800) : undefined,
      }),
    onSuccess: (res) => {
      setEffects(res.effects ?? null);
      setCompleteError(null);
      onCompleted(
        `${step.id} ${step.title}: mit Nachweis abgeschlossen — protokolliert als ${res.run_id} (siehe Agent Runs & Evidence).`,
      );
      qc.invalidateQueries({ queryKey: ["step-guide", effectiveTenantId, step.id] });
    },
    onError: (err: any) => {
      setCompleteError(stepErrorMessage(step, err));
    },
  });

  function submitComplete() {
    if (!evidenceNote.trim()) {
      setCompleteError("Bitte zuerst die Nachweis-Notiz ausfüllen (Bereich 4): Was wurde erledigt, wo liegt das Ergebnis?");
      evidenceRef.current?.focus();
      return;
    }
    setCompleteError(null);
    complete.mutate();
  }

  const raci = guide.data?.raci;
  const fragen = guide.data?.fragen ?? [];
  const contributions = guide.data?.contributions ?? [];
  const runs = guide.data?.runs ?? [];

  return (
    <div className="step-detail pl-guide">
      {/* Kompakt-Infos wie bisher */}
      <div>
        <span title="Arbeitsgrundlagen: Das braucht dieser Schritt, bevor er starten kann.">Eingaben</span>
        <p>{step.inputs.join(", ")}</p>
      </div>
      <div>
        <span title="Das liegt am Ende des Schritts vor (Ergebnisse bzw. Dokumente).">Ergebnisse</span>
        <p>{step.outputs.join(", ")}</p>
      </div>
      <div>
        <span title="Auslöser: In diesen Situationen wird der Schritt (auch automatisch) angestoßen.">Trigger</span>
        <p>{step.triggers.join(", ")}</p>
      </div>
      <div className="step-actions">
        <span title="Typische Bearbeitungszeit. 'automatisch' bedeutet: läuft ohne manuelles Zutun, sobald ein Auslöser eintritt.">Dauer: {step.duration}</span>
        {!isKunde && (
          <button
            type="button"
            className="btn ghost small"
            title="Legt einen protokollierten Durchlauf (Run) dieses Schritts an — sichtbar unter Agent Runs & Evidence. Der Schritt gilt damit noch nicht als abgeschlossen."
            disabled={stepActionPending === `start:${step.id}`}
            onClick={() => onStartStep(step)}
          >
            {stepActionPending === `start:${step.id}` ? "Starte Vorgang..." : "Vorgang starten"}
          </button>
        )}
        {agent && (
          <Link
            to={advisorLink(agent, step.title)}
            className="btn ghost small"
            title={`Öffnet den KI-Berater (${agent.name}) mit dem Kontext dieses Schritts — für Empfehlungen zum Vorgehen.`}
          >
            KI-Berater
          </Link>
        )}
        {doneStep && <span className="pill ok small">Abgeschlossen ✓</span>}
      </div>

      {/* BEN-92: einheitliche Deep-Links je Prozessschritt — Dashboard, OE-Board, OpenProject, Evidence */}
      <DeepLinkBar
        className="pl-step-links"
        label="Springen zu:"
        links={[
          {
            key: "sw",
            href: dashboardUrl("steuerung-wirkung"),
            label: "Wirkung im Dashboard",
            kind: "dashboard",
            title: "Steuerung & Wirkung — Runs, Methodik, Wirkungs-Map (OpenSearch)",
          },
          guide.data?.oe_board?.mapped
            ? {
                key: "oe",
                href: dashboardUrl("oe-board"),
                label: "OE-Board",
                kind: "dashboard",
                title: `OE-Board — Phase ${guide.data.oe_board.oe_phase ?? ""}`,
              }
            : null,
          {
            key: "op",
            href: openProjectProgramUrl(),
            label: "In OpenProject öffnen",
            kind: "openproject",
            title: "Arbeitspakete des 1&1-Programms in OpenProject öffnen",
          },
          {
            key: "ev",
            href: evidenceRoute(),
            label: "Belege & Runs",
            kind: "intern",
            title: "Agent Runs & Evidence ansehen",
          },
        ]}
      />

      {/* ---------------- Geführter Modus ---------------- */}
      <div className="pl-guide-full">
        {guide.isLoading && (
          <div className="pl-guide-skeleton">
            <div className="skeleton" style={{ height: 14, width: "45%" }} />
            <div className="skeleton" style={{ height: 14, width: "80%" }} />
            <div className="skeleton" style={{ height: 14, width: "65%" }} />
          </div>
        )}
        {guide.isError && (
          <p className="muted small">
            Der geführte Modus ist gerade nicht erreichbar (Server-Update oder Netzwerk). Die Basisfunktionen oben bleiben nutzbar — bitte später erneut öffnen.
          </p>
        )}
        {guide.data && (
          <>
            {/* 1 · Was passiert hier? */}
            <section className="pl-guide-block">
              <h4><span className="pl-guide-nr">1</span> Was passiert in diesem Schritt?</h4>
              <p>
                {guide.data.zweck || step.description}{" "}
                <span className="muted">
                  Zuständiger KI-Agent: <strong>{agent?.name ?? "—"}</strong> ({agent?.modelRoute ?? "lokal"}).
                </span>
              </p>
            </section>

            {/* 2 · Wer liefert was? */}
            <section className="pl-guide-block">
              <h4><span className="pl-guide-nr">2</span> Wer liefert was? <Hint term="raci" /></h4>
              <ul className="pl-guide-inputs">
                {(raci?.inputs_von ?? []).map((input) => (
                  <li key={input.rolle}>
                    <span className={`pill small ${input.rolle === "kunde" ? "neutral" : "ok"}`}>{input.label}</span>
                    <span>{input.liefert}</span>
                  </li>
                ))}
              </ul>
              <div className="pl-contribs">
                <strong className="small">Eingaben zu diesem Schritt ({contributions.length})</strong>
                {contributions.length === 0 && (
                  <p className="muted small">Noch keine Eingaben. Notieren Sie hier Zulieferungen, Beschlüsse oder Links zu Dokumenten (z.&nbsp;B. Nextcloud-Ablage).</p>
                )}
                <ul className="pl-contrib-list">
                  {contributions.slice(0, 6).map((con) => (
                    <li key={con.id}>
                      <span className={`pill small ${con.role === "kunde" ? "neutral" : "ok"}`}>{con.role === "kunde" ? "Kunde" : "Berater:in"}</span>
                      <span className="pl-contrib-kind">{con.kind}</span>
                      {con.url ? (
                        <a href={con.url} target="_blank" rel="noreferrer">{con.text || con.url}</a>
                      ) : (
                        <span>{con.text}</span>
                      )}
                    </li>
                  ))}
                </ul>
                <form
                  className="pl-contrib-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!contribText.trim() && !contribUrl.trim()) return;
                    addContribution.mutate({
                      kind: contribKind,
                      text: contribText.trim() || undefined,
                      url: contribUrl.trim() || undefined,
                    });
                  }}
                >
                  <select value={contribKind} onChange={(e) => setContribKind(e.target.value as "notiz" | "link" | "datei")} aria-label="Art der Eingabe">
                    <option value="notiz">Notiz</option>
                    <option value="link">Link</option>
                    <option value="datei">Datei-Referenz</option>
                  </select>
                  <input
                    type="text"
                    value={contribText}
                    onChange={(e) => setContribText(e.target.value)}
                    placeholder={contribKind === "datei" ? "Dateiname/Ablageort (z. B. Nextcloud-Pfad)" : "Kurze Notiz — was liegt vor?"}
                    aria-label="Eingabe-Text"
                  />
                  {(contribKind === "link" || contribKind === "datei") && (
                    <input
                      type="url"
                      value={contribUrl}
                      onChange={(e) => setContribUrl(e.target.value)}
                      placeholder="https://…"
                      aria-label="Link zur Eingabe"
                    />
                  )}
                  <button className="btn ghost small" type="submit" disabled={addContribution.isPending}>
                    {addContribution.isPending ? "Speichere…" : "Eingabe hinterlegen"}
                  </button>
                </form>
                {addContribution.isError && (
                  <p className="muted small">Eingabe konnte nicht gespeichert werden — bitte erneut versuchen.</p>
                )}
                <p className="muted small">
                  Datei-Uploads laufen weiterhin über die Dropzone/Nextcloud-Ablage; hier wird die Referenz verlinkt, damit der Schritt vollständig nachvollziehbar bleibt.
                </p>
              </div>
            </section>

            {/* 3 · Wie entsteht das Ergebnis? */}
            <section className="pl-guide-block">
              <h4><span className="pl-guide-nr">3</span> Wie entsteht das Ergebnis? (Analyse)</h4>
              <p className="muted small">Leitende Fragen ({guide.data.fragen_quelle}):</p>
              <ul className="pl-guide-questions">
                {fragen.map((frage) => <li key={frage}>{frage}</li>)}
              </ul>
              <div className="pl-knowledge">
                <div className="pl-knowledge-head">
                  <strong className="small">Relevante Grundlagen aus der Wissensbasis</strong>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      knowledge.refetch();
                    }}
                  >
                    <input
                      type="search"
                      value={knowledgeQuery}
                      onChange={(e) => setKnowledgeQuery(e.target.value)}
                      placeholder={`Suchen… (Standard: „${step.title}“)`}
                      aria-label="Wissensbasis durchsuchen"
                    />
                    <button className="btn ghost small" type="submit" disabled={knowledge.isFetching}>
                      {knowledge.isFetching ? "Suche…" : "Suchen"}
                    </button>
                  </form>
                </div>
                {knowledge.isLoading && (
                  <div className="pl-guide-skeleton">
                    <div className="skeleton" style={{ height: 12, width: "90%" }} />
                    <div className="skeleton" style={{ height: 12, width: "75%" }} />
                  </div>
                )}
                {knowledge.data && knowledge.data.items.length === 0 && (
                  <p className="muted small">
                    Keine Treffer in der Wissensbasis{knowledge.data.error ? ` (${knowledge.data.error})` : ""}. Neu ingestierte Dokumente (Dropzone/Docling) erscheinen hier automatisch.
                  </p>
                )}
                <ul className="pl-knowledge-list">
                  {(knowledge.data?.items ?? []).map((item, idx) => (
                    <li key={`${item.filename}-${idx}`}>
                      <strong>{item.filename || "Dokument"}</strong>
                      {item.snippet && <p className="muted small">{item.snippet}…</p>}
                      <span className="muted small">{item.path || item.source || ""}</span>
                    </li>
                  ))}
                </ul>
                {knowledge.data?.engine && (
                  <p className="muted small">Suche: {knowledge.data.engine} — findet auch neu ingestierte Inhalte in Echtzeit.</p>
                )}
              </div>
              <div className="pl-ki-begleitung" role="note">
                <span className="pl-ki-ic" aria-hidden>&#10035;</span>
                <div>
                  <strong>KI-Begleitung zu diesem Schritt</strong>
                  {contributions.length === 0 ? (
                    <p>Noch keine Eingaben hinterlegt &mdash; legen Sie in Bereich 2 eine Notiz, einen Link oder eine Datei-Referenz an, damit die KI-Analyse etwas Belegbares hat.</p>
                  ) : (
                    <p>{contributions.length} Eingabe(n) vorhanden. Prüfen Sie die passenden Interventionen unten und starten Sie die KI-Analyse &mdash; der Abschluss braucht einen Nachweis (Bereich 4).</p>
                  )}
                </div>
              </div>
              <InterventionEmpfehlungen situationText={(step as any).situation ?? step.title} autoRun />
              <div className="pl-decision-cta" style={{ margin: "8px 0" }}>
                <Link
                  to={`/app/decisions?new=1&title=${encodeURIComponent("Entscheidung: " + step.title)}&context=${encodeURIComponent("Aus Prozessschritt " + step.id + " (" + step.title + ").")}`}
                  className="btn ghost small"
                  title="Eine Entscheidung zu diesem Schritt strukturiert im Entscheidungs-Log festhalten."
                >
                  Entscheidung protokollieren →
                </Link>
              </div>
              <div className="pl-analyze">
                <button className="btn ghost small" type="button" disabled={analyze.isPending} onClick={() => analyze.mutate()}>
                  {analyze.isPending ? "KI-Analyse läuft…" : "KI-Analyse starten (lokales Modell)"}
                </button>
                {analyze.isError && (
                  <p className="muted small">Analyse derzeit nicht möglich — bitte später erneut versuchen.</p>
                )}
                {analysis && (
                  <div className={`pl-analysis ${analysis.degraded ? "degraded" : ""}`}>
                    <p>{analysis.answer}</p>
                    <p className="muted small">
                      {analysis.degraded
                        ? "Hinweis: Modell nicht erreichbar — ehrliche Teilantwort."
                        : `Modell: ${analysis.model ?? "lokal"} · genutzte Dokumente: ${analysis.knowledge_used.filter(Boolean).join(", ") || "keine"}`}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* 4 · Wer schließt ab? */}
            <section className="pl-guide-block">
              <h4><span className="pl-guide-nr">4</span> Wer schließt ab — und womit?</h4>
              <ul className="pl-guide-raci">
                <li><span>Verantwortlich</span><strong>{raci?.verantwortlich}</strong></li>
                <li><span>Rechenschaft</span><strong>{raci?.rechenschaft}</strong></li>
                <li><span>Konsultiert</span><strong>{raci?.konsultiert}</strong></li>
                <li><span>Informiert</span><strong>{raci?.informiert}</strong></li>
                <li><span>Abschluss</span><strong>{raci?.abschluss_durch}</strong></li>
              </ul>
              {isKunde ? (
                <p className="pl-role-hint">
                  Sie sehen diesen Schritt in der Kundensicht: Eingaben (Bereich 2) sind willkommen — den Abschluss mit Nachweis übernimmt Ihre Berater:in.
                </p>
              ) : doneStep ? (
                <p className="pl-role-hint ok">Dieser Schritt ist bereits mit Nachweis abgeschlossen.</p>
              ) : (
                <div className="pl-complete">
                  <label htmlFor={`evi-${step.id}`} className="small">
                    Nachweis-Notiz (Evidence) <Hint term="prozess_evidence" /> — was wurde erledigt, wo liegt das Ergebnis?
                  </label>
                  <textarea
                    id={`evi-${step.id}`}
                    ref={evidenceRef}
                    value={evidenceNote}
                    onChange={(e) => setEvidenceNote(e.target.value)}
                    rows={3}
                    placeholder={`z. B. „${step.outputs[0] ?? "Ergebnis"} final abgestimmt, Ablage: Nextcloud/…“`}
                  />
                  <button
                    className="btn primary small"
                    type="button"
                    disabled={complete.isPending}
                    onClick={submitComplete}
                    title="Schließt den Vorgang ab und hinterlegt die prüfbare Nachweis-Notiz. Der Schritt zählt dann in den Fortschritt; die Wirkung (Runs-Index, OE-Board, OpenProject-Plan) wird unten angezeigt."
                  >
                    {complete.isPending ? "Speichere Nachweis…" : "Mit Nachweis abschließen"}
                  </button>
                  {completeError && <p className="form-error">{completeError}</p>}
                </div>
              )}
            </section>

            {/* 5 · Was wird wo abgelegt? */}
            <section className="pl-guide-block">
              <h4><span className="pl-guide-nr">5</span> Was wird wo abgelegt?</h4>
              <ul className="pl-guide-ablage">
                {guide.data.ablage.map((row) => (
                  <li key={row.was}>
                    <strong>{row.was}</strong>
                    <span>{row.wo}</span>
                    <span className="muted small">Sichtbar: {row.sichtbar}</span>
                  </li>
                ))}
              </ul>
              <p className="muted small">{guide.data.oe_board.hinweis}</p>
              <p className="muted small">
                OpenProject: {guide.data.openproject.modus} — {guide.data.openproject.hinweis}
              </p>
              {runs.length > 0 && (
                <div className="pl-runs">
                  <strong className="small">Bisherige Vorgänge zu diesem Schritt</strong>
                  <ul>
                    {runs.map((run) => (
                      <li key={run.run_id}>
                        <Link to={`/app/agents?run=${encodeURIComponent(run.run_id)}`}>{run.run_id}</Link>
                        <span className={`pill small ${run.status === "succeeded" ? "ok" : "neutral"}`}>{run.status === "succeeded" ? "abgeschlossen" : run.status}</span>
                        {run.evidence_note && <span className="muted small">„{run.evidence_note.slice(0, 80)}“</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {effects && (
                <div className="pl-effects">
                  <strong className="small">Wirkung dieses Abschlusses (live):</strong>
                  <ul>
                    <li>
                      {effects.runs_index?.written
                        ? `✓ Run-Dokument im Auswertungs-Index ${effects.runs_index.index} geschrieben`
                        : `– Runs-Index: ${effects.runs_index?.reason ?? "nicht geschrieben"}`}
                    </li>
                    <li>
                      {effects.oe_board?.applied
                        ? `✓ OE-Board fortgeschrieben (${effects.oe_board.doc_id}, Quelle process-run)`
                        : `– OE-Board: ${effects.oe_board?.reason ?? "keine Zuordnung"}`}
                    </li>
                    <li>
                      {effects.openproject?.mode === "dry_run" ? (
                        <>
                          ◻ OpenProject <strong>Dry-Run</strong>: Kommentar an{" "}
                          {effects.openproject?.wp?.url ? (
                            <a href={effects.openproject.wp.url} target="_blank" rel="noreferrer">
                              WP #{effects.openproject.wp.wp_id} „{effects.openproject.wp.subject}“
                            </a>
                          ) : (
                            "kein WP zugeordnet"
                          )}{" "}
                          wäre geplant — es wurde <strong>nichts</strong> geschrieben (Freigabe der Inhaberin erforderlich).
                        </>
                      ) : effects.openproject?.mode === "live" ? (
                        `✓ OpenProject-Kommentar geschrieben (WP #${effects.openproject?.wp?.wp_id})`
                      ) : (
                        `– OpenProject: ${effects.openproject?.reason ?? effects.openproject?.mode ?? "nicht angewendet"}`
                      )}
                    </li>
                  </ul>
                  {effects.openproject?.mode === "dry_run" && effects.openproject?.comment_preview && (
                    <p className="pl-op-preview muted small">Geplanter Kommentar: „{effects.openproject.comment_preview}“</p>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function advisorLink(agent: ProcessAgent, stepTitle?: string) {
  const msg = stepTitle
    ? `Berate mich als ${agent.name} für den Prozessschritt "${stepTitle}". Welche Eingaben, Quality-Gates, Risiken, Ergebnisse und nächsten HITL-Entscheidungen sind jetzt relevant?`
    : `Berate mich als ${agent.name}. Welche proaktiven Trigger, Datenquellen, Risiken, Belege und nächsten Schritte sind für KPI Enterprise Mining relevant?`;
  const params = new URLSearchParams({ mode: "strategy", persona: "Berater", msg });
  return `/app/advisor?${params.toString()}`;
}
